import { useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { queryModel } from 'services/ModelService';
import {
  setBomItems,
  setCustomNetPrice,
  setExtraPriceQuestions,
  setImages,
  setIsUsingSupplierSystemDiscounts,
  setLang,
  setModel,
  setPricing,
  setQueryPayload,
  setQuestions,
  setResetDiscountLines,
  setVariables,
} from 'store/Model/actions/model';
import {
  GET_EDIT_CONFIGURATION_ATTEMPT,
  GET_EDIT_CONFIGURATION_ERROR,
  GET_EDIT_CONFIGURATION_SUCCESS,
  QUERY_ATTEMPT,
  QUERY_ERROR,
  QUERY_RESET,
  QUERY_SUCCESS,
} from 'store/constants';
import { IRootReducerState } from 'store/store';
import {
  useFormatFormValuesIntoQueryPayload,
  useModelIdChange,
  useIsEditMode,
  useValuesDidUpdate,
  useGetTaxRate,
  useGetBuilderEditVersionId,
} from './hooks';
import { getEditConfiguration } from 'services/ExternalService';
import {
  useGetValuesFromQuestionOutputs,
  useListenForAddToCartTrigger,
  useListenForEditCartItemTrigger,
  useListenForUpdateQuotationLineTrigger,
  useListenForSaveTemplateTrigger,
} from 'pages/ConfigurePage/ConfigureForm/hooks';
import { IQuestionDifferences, checkQuestionDifferences } from './validation';

import { setEditConfigurationValidationWarnings } from 'store/Common/actions/common';
import { toast } from 'react-toastify';
import i18n, { FALLBACK_LOCALE } from 'providers/i18n/i18n';
import { ITestCompanyModelConfiguration } from 'types/Configuration.types';
import { IConfigurationQuestion } from 'types/ConfigurationQuestion.types';
import { AppMode } from 'store/Common/reducers/common';
import {
  setIndicativeQuestionKeys,
  setSelectedConfigurationAnswers,
} from 'store/SubmitConfiguration/actions/submitConfiguration';
import {
  deriveIndicativeQuestionKeys,
  handleConfigurationQuestionsAfterObtainingEditConfiguration,
  handleConfigurationQuestionsAfterSuccessfulQuery,
  mergeDifferences,
} from './helpers';
import { resetDiscountsForChangedLines } from 'components/SubHeader/PricingSection/helpers';
import { useIntentConfiguration } from './hooks';
import { setCurrency } from 'store/ShoppingCart/actions/shoppingCart';
import { useTemplateConfigureParams } from 'utils/hooks/useTemplateConfigureParams';
import { setTemplateToApply } from 'store/SubmitConfiguration/actions/submitConfiguration';
import { getSingleTemplate } from 'services/TemplateService';

interface IQueryProviderProps {
  children: React.ReactNode;
}

// QueryProvider is placed inside the form context, wraps all FormItems and sends a query request to logix with the current form values if any value changes
const QueryProvider = ({ children }: IQueryProviderProps) => {
  const isEditMode = useIsEditMode(); // Edit with webhook
  const [hasObtainedEditConfiguration, setHasObtainedEditConfiguration] =
    useState<boolean>(false); // Edit with webhook

  // Reopening a saved template in the configurator
  const templateParams = useTemplateConfigureParams();
  const templateIdToApply = templateParams?.templateId ?? null;
  const [hasAppliedTemplate, setHasAppliedTemplate] = useState<boolean>(false);

  const dispatch = useDispatch();
  const { values } = useFormikContext();

  const { hasModelIdChanged, currentModelId } = useModelIdChange();

  const editVersionId: number | undefined = useGetBuilderEditVersionId();
  const taxRate: number | undefined = useGetTaxRate();
  const testConfiguration: ITestCompanyModelConfiguration | null = useSelector(
    (state: IRootReducerState) =>
      state.submitConfigurationInfo.testCompanyModelConfiguration
  );

  const includeBomItems = useSelector(
    (state: IRootReducerState) => state.formInfo.includeBomItems
  );

  // Lines only get their discount reset after the user actually changes a
  // property. During the initial intent load the configuration settles over
  // several automatic queries (gross values shift without any user input), and
  // resetting on those would drop discounts that NAV sent back correctly.
  const hasUserChangedQuestion = useSelector(
    (state: IRootReducerState) =>
      state.formInfo.manuallyChangedQuestionNames.length > 0
  );

  const {
    queryStatus: queryRequestStatus,
    pricing,
    customNetPrice,
    resetDiscountLines,
    predefinedPricing,
  } = useSelector((state: IRootReducerState) => state.modelInfo);

  const didValuesChange = useValuesDidUpdate(
    values,
    currentModelId,
    testConfiguration,
    taxRate,
    includeBomItems,
    pricing,
    customNetPrice
  );
  const questionInitialKeysToExcludeInQueryRef = useRef<string[]>([]);
  // The answers as the last query response left them. ConfigureForm syncs the
  // form to these, so a value that differs from its entry here is one the user
  // has just changed - which is what tells an automatically filled answer apart
  // from one the user chose.
  const valuesFromOutputs = useGetValuesFromQuestionOutputs();
  const queryPayload = useFormatFormValuesIntoQueryPayload(
    values as object,
    questionInitialKeysToExcludeInQueryRef,
    valuesFromOutputs
  );

  useEffect(() => {
    if (queryRequestStatus.success || queryRequestStatus.error) {
      dispatch({ type: QUERY_RESET });
    }
  }, [queryRequestStatus]);

  const {
    base64Metadata,
    selectedConfigurationAnswers,
    clearedExtraPriceQuestionSlugs,
    intentUUID,
    templateToApply,
  } = useSelector((state: IRootReducerState) => state.submitConfigurationInfo);

  // The regular query loop must not run while a template is being loaded. On
  // first mount it would query with an empty payload and dispatch a set of
  // default answers that the template response then races to overwrite; mid
  // session it would fire a competing query off a half-applied payload.
  const isWaitingForTemplate =
    (!!templateIdToApply && !hasAppliedTemplate) || !!templateToApply;
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const intentConfiguration = useIntentConfiguration();

  // In ConfigureForm, new values are set if the query response outputs and the current formik values missmatch
  useEffect(() => {
    (async () => {
      if (isWaitingForTemplate) {
        return;
      }
      if ((isEditMode && hasObtainedEditConfiguration) || !isEditMode) {
        if (!queryRequestStatus.attempt && currentModelId) {
          if (
            didValuesChange ||
            questionInitialKeysToExcludeInQueryRef.current.length // Required in order to trigger the query request after fetching edit data
          ) {
            try {
              dispatch({ type: QUERY_ATTEMPT });
              const lang = i18n.resolvedLanguage || FALLBACK_LOCALE;
              const queryModelPromise = queryModel(
                currentModelId,
                queryPayload,
                lang,
                taxRate,
                appMode === AppMode.DEALER_PANEL || appMode === AppMode.BUYER
                  ? testConfiguration
                  : null,
                editVersionId,
                includeBomItems,
                pricing,
                customNetPrice,
                appMode === AppMode.BUYER ? AppMode.BUYER : null,
                predefinedPricing
              );
              const [queryData] = await Promise.all([queryModelPromise]);
              if (questionInitialKeysToExcludeInQueryRef.current.length) {
                questionInitialKeysToExcludeInQueryRef.current = [];
              }
              dispatch({ type: QUERY_SUCCESS });
              dispatch(
                setModel({
                  general: queryData.general,
                  id: queryData.id,
                  version_number: queryData.version_number,
                  round_pricing: queryData.round_pricing,
                  conn_lead_mpr: queryData.conn_lead_mpr,
                  conn_lead_time: queryData.conn_lead_time,
                  has_bom_items: queryData.has_bom_items,
                })
              );
              dispatch(setCurrency(queryData.currency));
              dispatch(setQuestions(queryData.questions));
              dispatch(setQueryPayload(queryPayload.questions));
              dispatch(setLang(lang));
              dispatch(setImages(queryData.images));

              // Handle discount resets for line with custom discount/amount when gross price is changed, only on intent configuration and only on first change
              let finalPricing = queryData.pricing;
              if (intentConfiguration && hasUserChangedQuestion) {
                const { updatedPricing, newResetLines } =
                  resetDiscountsForChangedLines(
                    queryData.pricing,
                    pricing,
                    resetDiscountLines
                  );
                finalPricing = updatedPricing;
                if (newResetLines.size > 0) {
                  dispatch(setResetDiscountLines(newResetLines));
                }
              }

              dispatch(setPricing(finalPricing));

              dispatch(setBomItems(queryData.bom_items));
              dispatch(setVariables(queryData.variables));
              handleConfigurationQuestionsAfterSuccessfulQuery(
                dispatch,
                selectedConfigurationAnswers,
                clearedExtraPriceQuestionSlugs,
                queryData?.extra_price_questions || null
              );
              dispatch(
                setIsUsingSupplierSystemDiscounts(
                  queryData.is_using_seller_system_discounts
                )
              );
            } catch (error: any) {
              if (error?.response?.data?.message) {
                // Display error message if model has been disabled for this company
                toast.error(error.response.data.message, {
                  toastId: 'QUERY_ERROR',
                });
              }
              dispatch({ type: QUERY_ERROR });
              dispatch(setQuestions({}));
              dispatch(setQueryPayload({}));
              dispatch(setImages({}));
              dispatch(setPricing({}));
              dispatch(setCustomNetPrice(undefined));
              dispatch(setVariables(undefined));
              dispatch(setExtraPriceQuestions([]));
              dispatch(setSelectedConfigurationAnswers([]));
              dispatch(setIsUsingSupplierSystemDiscounts(false));
              dispatch(setResetDiscountLines(new Set<string>()));
            }
          }
        }
      }
    })();
  }, [
    hasObtainedEditConfiguration,
    hasAppliedTemplate,
    didValuesChange,
    hasModelIdChanged,
    queryPayload, // This is in the dep array because didValuesChange can be true in 2 consecutive renders, so it might not trigger the query request when it should have
  ]);

  useEffect(() => {
    (async () => {
      if (isEditMode && intentUUID && !hasObtainedEditConfiguration) {
        try {
          dispatch({ type: GET_EDIT_CONFIGURATION_ATTEMPT });
          const getEditConfigurationPromise =
            getEditConfiguration(base64Metadata);
          const [editConfigurationData] = await Promise.all([
            getEditConfigurationPromise,
          ]);
          dispatch({ type: GET_EDIT_CONFIGURATION_SUCCESS });

          // After obtaining existing configuration, fetch the newest questions for the model,
          // and check for differences in questions and options
          const {
            differences,
            extraPriceQuestions,
          }: {
            differences: IQuestionDifferences;
            extraPriceQuestions: IConfigurationQuestion[] | null;
          } = await checkQuestionDifferences(editConfigurationData);

          const configurationQuestionDifferences: IQuestionDifferences =
            handleConfigurationQuestionsAfterObtainingEditConfiguration(
              dispatch,
              extraPriceQuestions,
              editConfigurationData
            );
          const mergedDifferences = mergeDifferences(
            differences,
            configurationQuestionDifferences
          );

          if (mergedDifferences) {
            dispatch(setEditConfigurationValidationWarnings(mergedDifferences));
            const excludedQuestionKeys = [
              ...differences.missingQuestionsKeys,
              ...differences.extraQuestionsKeys,
              ...differences.matchingQuestionsWithInvalidOutputsKeys,
            ];
            questionInitialKeysToExcludeInQueryRef.current =
              excludedQuestionKeys;
          }

          dispatch(setQuestions(editConfigurationData.questions));
          dispatch(
            setIndicativeQuestionKeys(
              deriveIndicativeQuestionKeys(editConfigurationData.questions)
            )
          );
          dispatch(setBomItems(editConfigurationData.bom_items));
          setHasObtainedEditConfiguration(true);
        } catch (error) {
          dispatch({ type: GET_EDIT_CONFIGURATION_ERROR });
          dispatch(setQuestions({}));
          dispatch(setPricing({}));
          dispatch(setCustomNetPrice(undefined));
          dispatch(setBomItems({}));
        }
      }
    })();
  }, [isEditMode, intentUUID]);

  // Loading a template into the form. A template stores a query payload, not
  // questions, so it cannot simply be dispatched the way an edit configuration
  // or a quotation line is: it has to be run through the model first. Querying
  // with the saved payload comes back with those answers as outputs, which is
  // what useGetValuesFromQuestionOutputs turns into the form values.
  //
  // Shared by both entry points — reopening a saved template from the dealer
  // panel (?templateId=) and picking one in the quotation configurator — so the
  // two cannot drift.
  const applyTemplateToForm = async (templateId: number) => {
    try {
      dispatch({ type: QUERY_ATTEMPT });
      const template = await getSingleTemplate(templateId);
      const lang = i18n.resolvedLanguage || FALLBACK_LOCALE;
      // The model in scope stays the source of truth so the follow-up queries
      // cannot diverge; template.model_id is expected to match it.
      //
      // Spread queryPayload rather than sending `{ questions }` alone: it also
      // carries company_id / company_mode, which the backend requires whenever
      // there is no intent_uuid. Its own `questions` is replaced wholesale by
      // the template's answers — applying a template overwrites the form.
      const queryData = await queryModel(
        currentModelId ?? String(template.model_id),
        { ...queryPayload, questions: template.questions },
        lang,
        // Required by the backend for dealer-panel configuration intents, and
        // 0 in template mode — see useGetTaxRate.
        taxRate
      );
      dispatch({ type: QUERY_SUCCESS });
      dispatch(
        setModel({
          general: queryData.general,
          id: queryData.id,
          version_number: queryData.version_number,
          round_pricing: queryData.round_pricing,
          conn_lead_mpr: queryData.conn_lead_mpr,
          conn_lead_time: queryData.conn_lead_time,
          has_bom_items: queryData.has_bom_items,
        })
      );
      dispatch(setCurrency(queryData.currency));
      dispatch(setQuestions(queryData.questions));
      dispatch(setQueryPayload(template.questions));
      dispatch(setLang(lang));
      dispatch(setImages(queryData.images));
      dispatch(setPricing(queryData.pricing));
      dispatch(setBomItems(queryData.bom_items));
      dispatch(setVariables(queryData.variables));
      handleConfigurationQuestionsAfterSuccessfulQuery(
        dispatch,
        selectedConfigurationAnswers,
        clearedExtraPriceQuestionSlugs,
        queryData?.extra_price_questions || null
      );
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message, {
          toastId: 'APPLY_TEMPLATE_ERROR',
        });
      }
      dispatch({ type: QUERY_ERROR });
    }
  };

  // Entry point 1: reopening a saved template from the dealer panel.
  useEffect(() => {
    (async () => {
      if (!templateIdToApply || hasAppliedTemplate) {
        return;
      }
      await applyTemplateToForm(Number(templateIdToApply));
      // Either way the regular loop takes over — on failure the user gets an
      // empty form rather than one stuck behind the gate.
      setHasAppliedTemplate(true);
    })();
  }, [templateIdToApply]);

  // Entry point 2: picking a template in the quotation configurator. The id
  // doubles as the in-flight flag, so clearing it is what releases the regular
  // query loop.
  useEffect(() => {
    (async () => {
      if (!templateToApply) {
        return;
      }
      await applyTemplateToForm(templateToApply);
      dispatch(setTemplateToApply(null));
    })();
  }, [templateToApply]);

  useListenForAddToCartTrigger(queryPayload, pricing, dispatch);
  useListenForEditCartItemTrigger(queryPayload, pricing, dispatch);
  useListenForUpdateQuotationLineTrigger(queryPayload, dispatch);
  useListenForSaveTemplateTrigger(queryPayload, dispatch);

  return <>{children}</>;
};

export default QueryProvider;
